<template>
	<UPageSection
		:id="`page-section-${copy.id}`"
		orientation="horizontal"
		:reverse="reverse"
		:ui="ui"
	>
		<template #leading>
			<div
				ref="iconRef"
				class="size-16 shrink-0 text-primary inline-flex items-center justify-center"
			>
				<Motion
					v-if="shouldLoadIcon && icon"
					as-child
					:while-in-view="{ opacity: 1, rotate: 0 }"
					:initial="{ opacity: 0, rotate: 30 }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1.5, delay: 0.6 }"
					:in-view-options="{ once: true }"
				>
					<UIcon
						:name="icon"
						class="size-16 shrink-0 text-primary"
					/>
				</Motion>
				<div
					v-else-if="icon"
					class="size-16 shrink-0 bg-gray-200 rounded will-change-transform"
					:style="{
						opacity: 0,
						transform: 'rotate(30deg)'
					}"
				/>
			</div>
		</template>
		<!-- Rest of your existing template stays exactly the same -->
		<template #title>
			<motion.p
				v-if="copy.content.title"
				:while-in-view="{ opacity: 1 }"
				:initial="{ opacity: 0 }"
				:transition="{ delay: 0.4 }"
				:in-view-options="{ once: true }"
				:class="[
					ui.title,
					copy.content.orangeTitleFirst ? 'text-primary' : 'text-highlighted'
				]"
			>
				{{ copy.content.title }} <span :class="[copy.content.orangeTitleFirst ? 'text-highlighted' : 'text-primary']">
					{{ copy.content.titleOneConcat }}
				</span>
			</motion.p>
			<motion.p
				v-if="copy.content.titleTwo"
				:while-in-view="{ opacity: 1 }"
				:initial="{ opacity: 0 }"
				:transition="{ delay: 0.5 }"
				:in-view-options="{ once: true }"
				:class="[
					ui.titleTwo,
					copy.content.orangeTitleFirst ? 'text-highlighted' : 'text-primary'
				]"
			>
				{{ copy.content.titleTwo }} {{ titleTwoConcat }}
			</motion.p>
		</template>
		<slot name="aside">
			<Motion
				v-if="image"
				as-child
				:while-in-view="{ opacity: 1 }"
				:initial="{ opacity: 0 }"
				:transition="{ duration: 0.4, delay: 0.2 }"
				:in-view-options="{ once: true }"
			>
				<SvgImage
					:id="`svg-image-${copy.id}`"
					:image="{ ...image, densities: 'x1' }"
					:lines="lines"
					:circles="circles"
					:viewbox="viewbox"
					:class="imageClasses"
					:lazy="lazy"
					sizes="280px lg:260px xl:406px"
				/>
			</Motion>
		</slot>
		<template #description>
			<motion.p
				v-if="copy.content.description"
				:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
				:initial="{ opacity: 0, x: 10 }"
				:transition="{ duration: 0.4, delay: 0.5 }"
				:class="ui.description"
				:in-view-options="{ once: true }"
			>
				{{ copy.content.description }}
			</motion.p>
		</template>
		<template #features>
			<ul
				v-if="copy.features?.length"
				class="grid gap-4"
			>
				<li
					v-for="(feature, index) in copy.features"
					:key="index"
					data-orientation="horizontal"
					class="relative flex items-start gap-2.5"
				>
					<div class="inline-flex items-center justify-center p-0.5">
						<Motion
							v-if="feature.icon"
							as-child
							:while-in-view="{ scale: 1, opacity: 1 }"
							:initial="{ scale: 2, opacity: 0 }"
							:transition="{ type: 'spring', bounce: 0.5, duration: 0.7, delay: 0.6 }"
							:in-view-options="{ once: true }"
						>
							<UIcon
								:name="feature.icon || 'i-lucide:check'"
								class="size-6 shrink-0 text-primary"
								aria-hidden="true"
							/>
						</Motion>
					</div>
					<div>
						<motion.div
							v-if="feature.title"
							:while-in-view="{ opacity: 1 }"
							:initial="{ opacity: 0 }"
							:transition="{ delay: 0.7 }"
							:class="ui.features"
							:in-view-options="{ once: true }"
							class="text-pretty font-semibold text-highlighted text-xl"
						>
							{{ feature.title }}
						</motion.div>
						<motion.div
							v-if="feature.description"
							:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
							:initial="{ opacity: 0, x: 10 }"
							:transition="{ delay: 0.8 }"
							:class="ui.features"
							:in-view-options="{ once: true }"
							class="text-lg text-pretty text-muted mt-1 will-change-transform"
						>
							{{ feature.description }}
						</motion.div>
					</div>
				</li>
			</ul>
			<slot name="cta" />
		</template>
	</UPageSection>
</template>

<script setup lang="ts">
import type {
    SectionItem, SectionItemCopy
} from '@/types/components/section-item'
import {
    motion, Motion
} from 'motion-v'

defineProps({
	copy: {
		type: Object as PropType<SectionItemCopy>,
		required: true
	},
	icon: {
		type: String,
		default: ''
	},
	viewbox: {
		type: Object as PropType<SectionItem['viewbox']>,
		default: () => ({
			width: 405,
			height: 405
		})
	},
	image: {
		type: Object as PropType<SectionItem['image']>,
		default: () => ({
			url: '/home_content_aside_1.webp'
		})
	},
	lazy: {
		type: Boolean,
		default: false
	},
	lines: {
		type: Array as PropType<SectionItem['lines']>,
		default: () => ([])
	},
	circles: {
		type: Array as PropType<SectionItem['circles']>,
		default: () => ([])
	},
	reverse: {
		type: Boolean,
		default: true
	},
	ui: {
		type: Object,
		default: () => ({
			container: 'lg:items-center'
		})
	},
	titleTwoConcat: {
		type: String,
		default: ''
	},
	imageClasses: {
		type: String,
		default: 'flex shrink-0 w-full justify-center items-center max-w-[280px] sm:max-w-[280px] lg:max-w-[260px] xl:max-w-[405px] mx-auto mt-16 lg:mt-0'
	}
})

// Lazy loading logic for the icon
const iconRef = ref()
const shouldLoadIcon = ref(false)

onMounted(() => {
	const observer = new IntersectionObserver(
		entries => {
			if (entries?.[0]?.isIntersecting) {
				shouldLoadIcon.value = true
				observer.disconnect()
			}
		},
		{
			rootMargin: '100px', // Load when 100px before coming into view
			threshold: 0.1
		}
	)

	if (iconRef.value) {
		observer.observe(iconRef.value)
	}

	onUnmounted(() => {
		observer.disconnect()
	})
})
</script>
